"use client";

import { useState, useEffect } from "react";
import { useLocale } from "@/lib/useLocale";
import { t } from "@/lib/translations";

export default function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const { locale } = useLocale();

  useEffect(() => {
    const consent = localStorage.getItem("cookie-consent");
    if (!consent) {
      setIsVisible(true);
      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimateIn(true);
        });
      });
      return () => cancelAnimationFrame(raf);
    }
  }, []);

  const acceptAll = () => {
    setAnimateIn(false);
    setTimeout(() => {
      localStorage.setItem("cookie-consent", "accepted");
      setIsVisible(false);
    }, 300);
  };

  const decline = () => {
    setAnimateIn(false);
    setTimeout(() => {
      localStorage.setItem("cookie-consent", "declined");
      setIsVisible(false);
    }, 300);
  };

  if (!isVisible) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-end sm:items-center justify-center transition-all duration-300 ${
        animateIn
          ? "bg-black/60 backdrop-blur-sm"
          : "bg-black/0 backdrop-blur-none"
      }`}
      onClick={(e) => e.target === e.currentTarget && decline()}
    >
      <div
        className={`w-full sm:max-w-lg mx-4 sm:mx-auto bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl transition-all duration-300 ${
          animateIn
            ? "translate-y-0 opacity-100 scale-100"
            : "translate-y-8 opacity-0 scale-95"
        }`}
      >
        <div className="h-1 bg-gradient-to-r from-brand-600 via-gold-500 to-brand-600 rounded-t-3xl sm:rounded-t-2xl" />

        <div className="p-8 sm:p-10">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-50 to-gold-50 flex items-center justify-center mx-auto mb-6">
            <svg
              className="w-8 h-8 text-brand-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>

          <h3 className="text-2xl font-bold font-display text-gray-900 text-center mb-3">
            {t(locale, "cookie.title")}
          </h3>

          <p className="text-sm text-slate-600 leading-relaxed text-center mb-8">
            {t(locale, "cookie.body")}
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={decline}
              className="flex-1 px-6 py-3.5 rounded-xl border-2 border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 hover:border-slate-300 transition-all duration-200"
            >
              {t(locale, "cookie.decline")}
            </button>
            <button
              onClick={acceptAll}
              className="flex-1 px-6 py-3.5 rounded-xl bg-brand-950 text-white text-sm font-semibold hover:bg-brand-900 transition-all duration-200 shadow-lg shadow-brand-950/20"
            >
              {t(locale, "cookie.accept")}
            </button>
          </div>

          <p className="text-xs text-slate-400 text-center mt-6">
            {t(locale, "cookie.preferences")}
          </p>
        </div>
      </div>
    </div>
  );
}
